import { useState, useRef } from 'react';

interface EstimateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EstimateModal({ isOpen, onClose }: EstimateModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const encoded = new URLSearchParams(formData as unknown as Record<string, string>).toString();

    setSubmitting(true);
    try {
      const res = await fetch('https://readdy.ai/api/form/d71fde9lum2co2q1fmv0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encoded,
      });
      if (res.ok) {
        setSubmitted(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setCharCount(0);
    formRef.current?.reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
      <div
        className="relative bg-white rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-primary-dark px-7 py-6 flex items-center justify-between rounded-t-xl">
          <div>
            <h3 className="text-xl font-black uppercase text-white tracking-wide" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Request a Free Estimate
            </h3>
            <p className="text-primary-light/70 text-xs mt-1">We&apos;ll get back to you within 24 hours</p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors duration-200 cursor-pointer rounded-md hover:bg-white/10"
            aria-label="Close modal"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="px-7 py-7">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center bg-primary-light rounded-full mx-auto mb-4">
                <i className="ri-check-line text-3xl text-primary"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h4>
              <p className="text-gray-500 text-sm mb-6">Your estimate request has been received. We&apos;ll be in touch shortly.</p>
              <button
                onClick={handleClose}
                className="bg-primary hover:bg-primary-dark text-white text-xs font-bold uppercase tracking-widest px-8 py-3 rounded-md transition-colors duration-200 cursor-pointer whitespace-nowrap btn-press"
              >
                Close
              </button>
            </div>
          ) : (
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              data-readdy-form
              className="flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2">
                    Full Name <span className="text-primary">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="John Smith"
                    className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-primary transition-colors duration-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2">
                    Email <span className="text-primary">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-primary transition-colors duration-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="(269) 555-0000"
                  className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-primary transition-colors duration-200"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2">
                  Service Type <span className="text-primary">*</span>
                </label>
                <select
                  name="service_type"
                  required
                  defaultValue=""
                  className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-primary transition-colors duration-200 cursor-pointer"
                >
                  <option value="" disabled>Select a service...</option>
                  <option>Home Additions</option>
                  <option>Interior and Exterior Renovations</option>
                  <option>Pole Barns</option>
                  <option>General Contracting</option>
                  <option>Window Installation</option>
                  <option>Siding Installation</option>
                  <option>Kitchen Remodel</option>
                  <option>Bathroom Remodel</option>
                  <option>Fire Restoration</option>
                  <option>Other</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2">
                  Project Details
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="Describe your project..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-primary transition-colors duration-200 resize-none"
                ></textarea>
                <div className="text-right text-xs text-gray-400 mt-1">{charCount}/500</div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="bg-primary hover:bg-primary-dark disabled:bg-primary/50 text-white text-xs font-bold uppercase tracking-widest px-8 py-4 rounded-md transition-colors duration-200 cursor-pointer whitespace-nowrap w-full mt-1 btn-press"
              >
                {submitting ? 'Sending...' : 'Submit Request'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
